import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { Confetti } from "@/components/Confetti";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useCredits } from "@/hooks/useCredits";
import { supabase } from "@/integrations/supabase/client";
import { 
  CheckCircle2, 
  Sparkles, 
  ArrowRight, 
  CreditCard, 
  Gift,
  Zap
} from "lucide-react";

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { balance, creditsLoading } = useCredits();
  const [paymentDetails, setPaymentDetails] = useState<{
    credits: number;
    packName: string;
    amount: number;
  } | null>(null);
  const [showConfetti, setShowConfetti] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPaymentDetails = async () => {
      // Try to get order_id from localStorage
      const orderId = localStorage.getItem('pending_order_id');
      
      if (!orderId) {
        setIsLoading(false);
        return;
      }

      // Poll for completed payment (webhook might take a moment)
      let attempts = 0;
      const maxAttempts = 10;
      
      const checkPayment = async () => {
        const { data, error } = await supabase
          .from("pending_payments")
          .select("*")
          .eq("order_id", orderId)
          .maybeSingle();

        if (data && !error) {
          if (data.status === 'completed') {
            setPaymentDetails({
              credits: data.credits,
              packName: data.pack_name,
              amount: data.amount,
            });
            localStorage.removeItem('pending_order_id');
            setIsLoading(false);
          } else if (attempts < maxAttempts) {
            attempts++;
            setTimeout(checkPayment, 2000);
          } else {
            // Still pending after max attempts - show generic success
            setPaymentDetails({
              credits: data.credits,
              packName: data.pack_name,
              amount: data.amount,
            });
            setIsLoading(false);
          }
        } else {
          setIsLoading(false);
        }
      };

      checkPayment();
    };

    fetchPaymentDetails();

    // Stop confetti after 5 seconds
    const timer = setTimeout(() => setShowConfetti(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("fr-FR").format(price);
  };

  return (
    <AppLayout title="Paiement réussi">
      {showConfetti && <Confetti />}

      <div className="flex flex-col items-center justify-center min-h-[70vh] p-6">
        {/* Success Icon */}
        <div className="relative mb-8 animate-scale-in">
          <div className="w-24 h-24 rounded-full bg-success/20 flex items-center justify-center animate-pulse-glow">
            <CheckCircle2 className="w-14 h-14 text-success" />
          </div>
          <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-gradient-floop flex items-center justify-center animate-bounce">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-3xl md:text-4xl font-bold text-foreground text-center mb-3 animate-fade-up font-display">
          Paiement{" "}
          <span className="bg-gradient-floop bg-clip-text text-transparent">
            réussi !
          </span>
        </h1>
        <p className="text-muted-foreground text-center mb-8 animate-fade-up stagger-1">
          Merci pour votre achat. Vos crédits ont été ajoutés à votre compte.
        </p>

        {/* Payment Summary Card */}
        <Card className="w-full max-w-md bg-card border-border mb-8 animate-fade-up stagger-2">
          <CardContent className="p-6 space-y-4">
            {paymentDetails ? (
              <>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                      <Gift className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Pack acheté</p>
                      <p className="text-sm text-muted-foreground">
                        {paymentDetails.packName}
                      </p>
                    </div>
                  </div>
                  <Badge className="bg-success/20 text-success border-0">
                    Confirmé
                  </Badge>
                </div>

                <div className="h-px bg-border" />

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-accent/20 flex items-center justify-center">
                      <Zap className="w-5 h-5 text-accent" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Crédits ajoutés</p>
                      <p className="text-sm text-muted-foreground">
                        Disponibles immédiatement
                      </p>
                    </div>
                  </div>
                  <span className="text-2xl font-bold text-primary">
                    +{paymentDetails.credits}
                  </span>
                </div>

                <div className="h-px bg-border" />

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-highlight/20 flex items-center justify-center">
                      <CreditCard className="w-5 h-5 text-highlight" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Montant payé</p>
                      <p className="text-sm text-muted-foreground">
                        Mobile Money
                      </p>
                    </div>
                  </div>
                  <span className="font-bold text-foreground">
                    {formatPrice(paymentDetails.amount)} FCFA
                  </span>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Current Balance */}
        <div className="bg-gradient-floop/10 border border-primary/30 rounded-2xl p-4 mb-8 w-full max-w-md animate-fade-up stagger-3">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Votre solde actuel</span>
            {creditsLoading ? (
              <div className="animate-spin w-5 h-5 border-2 border-primary border-t-transparent rounded-full" />
            ) : (
              <span className="text-3xl font-bold bg-gradient-floop bg-clip-text text-transparent">
                {balance} crédits
              </span>
            )}
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 w-full max-w-md animate-fade-up stagger-4">
          <Button
            onClick={() => navigate("/create")}
            className="flex-1 bg-gradient-floop hover:opacity-90 text-white rounded-xl h-12 group"
          >
            Commencer à créer
            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
          <Button
            onClick={() => navigate("/history")}
            variant="outline"
            className="flex-1 rounded-xl h-12"
          >
            Voir l'historique
          </Button>
        </div>
      </div>
    </AppLayout>
  );
}
